import * as THREE from "three";

// The toon look contract (from the superposition spike's hero, gate 2): the GLSL pieces every toon material includes so
// characters, weapons and props share one cartoon shading, and the helpers for its constant-pixel outline hull. The
// pieces go into a MeshToonMaterial through patchLit's extras (lit-patch.ts):
//
//   fragDecl:     <the material's own declarations> + TOON_DECL
//   afterColor:   ... then `spAlbedo = diffuseColor.rgb;`
//   afterEmissive: RIM (optional) + the material's own emission
//   fragReplace:  ["#include <gradientmap_pars_fragment>", RAMP], ["#include <lights_fragment_begin>", LIGHTS_BEGIN],
//                 ["#include <opaque_fragment>", NEUTRAL_OUT]
//
// The material's own declarations must provide `float spFace` (0 off a face) and the uniforms `vec4 uRamp` (centre,
// half width, shadow level), `vec3 uShade` (shadow tint), and for RIM `vec3 uRimCol`, `float uRimK`, `vec3 uMoonView`
// (toward the key light, view space). TOON_DECL's uniforms (uShLift, uFaceLift, uNeutralShade, uNeutralLit) come from
// `shadowLiftUniforms` and the material's own table. The values are the game's (a `ToonLook`); this file owns none.
// The `sp` names are the spike's, kept so its programs stay exactly as they were.

/** The game's toon tuning: the numbers the pieces read through uniforms and the helpers below. */
export interface ToonLook {
  /** Terminator: centre in n·l, half width, shadow level. */
  ramp: [number, number, number];
  /** Shadow tint (multiplies the shadow level). */
  shade: THREE.ColorRepresentation;
  rim: THREE.ColorRepresentation;
  rimStrength: number;
  /** Outline: px at 1 m, min px, max px. */
  outline: [number, number, number];
  /** Outline LOD: the hull thins from `outlineFade[0]` m to nothing at `outlineFade[1]` m (hide it beyond). */
  outlineFade: [number, number];
  /** The hull pushed back from the camera (m), so a hull lying just over another surface stays behind it. */
  outlinePush: number;
  /** The outline's colour on skin (`hullColour`). */
  outlineSkin: THREE.ColorRepresentation;
  /** The shadow map's lookup lifted toward the light (m): on the body, on the face. */
  shadowLift: [number, number];
  /** n·l lifted on a face (its shadow arrives later than the body's). */
  faceLift: number;
  /** How much of the shadow tint goes on neutral greys and metals. */
  neutralShade: number;
  /** How far a lit neutral is pulled to its luminance. */
  neutralLit: number;
}

/** Declarations every toon material carries (after its own). */
export const TOON_DECL = /* glsl */ `
float spShadow = 1.0;    // the moon's shadow-map term, taken out of the light colour and put into the ramp
vec3 spAlbedo = vec3(1.0);
float spFaceN = 0.0;     // the face normal's weight (0 off the face)
uniform vec3 uShLift;
uniform float uFaceLift;
uniform float uNeutralShade;
uniform float uNeutralLit;
`;

const SHADOW_LINE =
  "directLight.color *= ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;";
/** three's light loop with the directional shadow moved into `spShadow` (the ramp reads it), its lookup lifted toward
 *  the light by uShLift (more on the face). One directional light casts. */
export const LIGHTS_BEGIN = THREE.ShaderChunk.lights_fragment_begin.replace(
  SHADOW_LINE,
  "spShadow = ( directLight.visible && receiveShadow ) ? getShadow( directionalShadowMap[ i ], directionalLightShadow.shadowMapSize, directionalLightShadow.shadowIntensity, directionalLightShadow.shadowBias - mix( uShLift.x, uShLift.y, spFaceN ) * uShLift.z, directionalLightShadow.shadowRadius, vDirectionalShadowCoord[ i ] ) : 1.0;",
);
/** Whether three's light loop matched the patch (false: shadows stay in the light colour, not the ramp). */
export const SHADOW_IN_RAMP = LIGHTS_BEGIN !== THREE.ShaderChunk.lights_fragment_begin;
if (!SHADOW_IN_RAMP) console.warn("[@rpg/look] three's shadow line changed: shadows stay in the light colour, not the ramp");

/** The ramp: two tones with a soft terminator; the shadow map folded in (a shadowed texel takes the same tinted shadow
 *  tone as a turned-away one); the tint a cool neutral on greys and metals; a face's n·l lifted. */
export const RAMP = /* glsl */ `
vec3 getGradientIrradiance(vec3 normal, vec3 lightDirection) {
  float d = dot(normal, lightDirection) + spFaceN * uFaceLift;
  float w = max(uRamp.y, fwidth(d));
  float lit = smoothstep(uRamp.x - w, uRamp.x + w, d) * smoothstep(0.2, 0.8, spShadow);
  vec3 s = pow(max(spAlbedo, 0.0), vec3(1.0 / 2.2));
  float mx = max(s.r, max(s.g, s.b)), mn = min(s.r, min(s.g, s.b));
  float neutral = (1.0 - smoothstep(0.06, 0.22, (mx - mn) / max(mx, 1e-3))) * smoothstep(0.25, 0.5, mx);
  vec3 grey = vec3(dot(uShade, vec3(0.2126, 0.7152, 0.0722))) * vec3(0.93, 0.97, 1.07);
  vec3 shade = mix(uShade, grey, neutral * uNeutralShade);
  return mix(shade * uRamp.z, vec3(1.0), lit);
}
`;

/** Neutral greys and metals keep their neutrality in the light too: a lit neutral albedo's colour is pulled part way to
 *  its own luminance (summed coloured lights otherwise tint steel). Replaces `#include <opaque_fragment>`. */
export const NEUTRAL_OUT = /* glsl */ `
{
  vec3 s = pow(max(spAlbedo, 0.0), vec3(1.0 / 2.2));
  float mx = max(s.r, max(s.g, s.b)), mn = min(s.r, min(s.g, s.b));
  float neutral = (1.0 - smoothstep(0.06, 0.22, (mx - mn) / max(mx, 1e-3))) * smoothstep(0.25, 0.5, mx);
  float l = dot(outgoingLight, vec3(0.2126, 0.7152, 0.0722));
  outgoingLight = mix(outgoingLight, vec3(l) * vec3(0.98, 0.99, 1.03), neutral * uNeutralLit);
}
#include <opaque_fragment>
`;

/** A key-side rim light (emissive; weaker on a face). */
export const RIM = /* glsl */ `
{
  float ndv = clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
  float fres = smoothstep(0.6, 0.68, 1.0 - ndv);
  float side = smoothstep(-0.1, 0.5, dot(normal, uMoonView));
  totalEmissiveRadiance += mix(uRimCol, diffuseColor.rgb * uRimCol * 2.5, 0.4) * fres * side * uRimK * (1.0 - 0.75 * spFace);
}
`;

/** The outline hull's vertex code (replaces `#include <project_vertex>`): pushed out along the welded normal `olN` by a
 *  constant pixel width that thins with distance, skinning aware. Needs HULL_DECL and `outlineUniforms`. */
export const HULL_VERT = /* glsl */ `
#include <project_vertex>
{
  vec3 olObj = olN;
  #ifdef USE_SKINNING
    olObj = (skinMatrix * vec4(olN, 0.0)).xyz;
  #endif
  vec3 vn = normalize(normalMatrix * olObj);
  float depth = -mvPosition.z;
  // constant pixels near, thinning past uOlFade.x to nothing at uOlFade.y (the hull is dropped beyond)
  float px = clamp(uOl.x / max(depth, 0.05), uOl.y, uOl.z) * (1.0 - smoothstep(uOlFade.x, uOlFade.y, depth));
  // pushed back from the camera, so a hull lying just over another surface stays behind it
  vec4 mvBack = mvPosition;
  mvBack.xyz += normalize(mvBack.xyz) * uOlPush;
  gl_Position = projectionMatrix * mvBack;
  vec2 dir = normalize(vn.xy + vec2(1e-5));
  gl_Position.xy += dir * px * 2.0 / uOlRes * gl_Position.w;
}
`;

/** The hull's vertex declarations (after `#include <common>`). */
export const HULL_DECL = "attribute vec3 olN;\nuniform vec3 uOl;\nuniform vec2 uOlRes;\nuniform vec2 uOlFade;\nuniform float uOlPush;";

/** The outline's uniforms (one set per look); set `uOlRes` to the drawing buffer's size each frame. */
export function outlineUniforms(look: Pick<ToonLook, "outline" | "outlineFade" | "outlinePush">): {
  uOl: THREE.IUniform<THREE.Vector3>;
  uOlRes: THREE.IUniform<THREE.Vector2>;
  uOlFade: THREE.IUniform<THREE.Vector2>;
  uOlPush: THREE.IUniform<number>;
} {
  return {
    uOl: { value: new THREE.Vector3(...look.outline) },
    uOlRes: { value: new THREE.Vector2(1280, 720) },
    uOlFade: { value: new THREE.Vector2(...look.outlineFade) },
    uOlPush: { value: look.outlinePush },
  };
}

/** The hull's fragment colour (after `#include <color_fragment>`): a darker, more saturated version of the surface's
 *  own; with `skin` given, that colour on skin-toned texels. */
export function hullColour(skin?: THREE.ColorRepresentation): string {
  const generic = "vec3 c = diffuseColor.rgb; float l = max(c.r, max(c.g, c.b)); vec3 o = max(mix(vec3(l), c, 1.5), 0.0) * 0.3;";
  if (skin === undefined) return `{ ${generic} diffuseColor.rgb = o; }`;
  const k = new THREE.Color(skin);
  return `{ ${generic}
    vec3 s = pow(max(c, 0.0), vec3(1.0 / 2.2));
    float mx = max(s.r, max(s.g, s.b)), mn = min(s.r, min(s.g, s.b));
    float sat = (mx - mn) / max(mx, 1e-3);
    float w = smoothstep(0.1, 0.18, s.r - s.b) * smoothstep(0.45, 0.58, s.r) * (1.0 - smoothstep(0.62, 0.72, sat)) * step(s.b - 0.03, s.g);
    diffuseColor.rgb = mix(o, vec3(${k.r.toFixed(4)}, ${k.g.toFixed(4)}, ${k.b.toFixed(4)}), w); }`;
}

/** The shadow lookup's lift uniform: (body m, face m, 1 / the shadow camera's depth range). Share one per scene. */
export function shadowLiftUniforms(lift: [number, number], depthScale = 1 / 139): { uShLift: THREE.IUniform<THREE.Vector3> } {
  return { uShLift: { value: new THREE.Vector3(lift[0], lift[1], depthScale) } };
}

/** Find the scene's shadow-casting directional light once (from `obj`'s onBeforeRender), and keep the lift's depth
 *  scale in step with its shadow camera (a lift in metres is a depth bias of lift / (far − near) in an orthographic
 *  shadow map). Replaces `obj.onBeforeRender`. */
export function trackShadowDepth(obj: THREE.Object3D, shadow: { uShLift: THREE.IUniform<THREE.Vector3> }): void {
  let light: THREE.DirectionalLight | null = null;
  obj.onBeforeRender = (_r, scene) => {
    if (!light)
      scene.traverse((o) => {
        if (!light && (o as THREE.DirectionalLight).isDirectionalLight && o.castShadow) light = o as THREE.DirectionalLight;
      });
    if (light) {
      const c = (light as THREE.DirectionalLight).shadow.camera;
      shadow.uShLift.value.z = 1 / Math.max(1e-3, c.far - c.near);
    }
  };
}

/** Smoothed normals per welded position (UV seams split vertices; an outline hull must not crack along them). */
export function weldedNormals(geo: THREE.BufferGeometry): Float32Array {
  const P = geo.getAttribute("position");
  const N = geo.getAttribute("normal");
  const key = (i: number): string => `${Math.round(P.getX(i) * 1e4)},${Math.round(P.getY(i) * 1e4)},${Math.round(P.getZ(i) * 1e4)}`;
  const sum = new Map<string, THREE.Vector3>();
  const keys: string[] = new Array(P.count);
  for (let i = 0; i < P.count; i++) {
    const k = (keys[i] = key(i));
    const v = sum.get(k) ?? sum.set(k, new THREE.Vector3()).get(k)!;
    v.x += N.getX(i);
    v.y += N.getY(i);
    v.z += N.getZ(i);
  }
  const out = new Float32Array(P.count * 3);
  for (let i = 0; i < P.count; i++) {
    const v = sum.get(keys[i]!)!.clone().normalize();
    out.set([v.x, v.y, v.z], i * 3);
  }
  return out;
}
